import React, { useState, useEffect, useCallback } from "react";
import { useAuth } from "../context/AuthContext";
import { api, CURRENCIES, formatApiErrorDetail } from "../lib/api";
import { toast } from "sonner";
import { KeyRound, Shield, Users } from "lucide-react";

function PasswordCard() {
  const [form, setForm] = useState({ current: "", next: "", confirm: "" });
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (form.next !== form.confirm) {
      toast.error("New passwords do not match");
      return;
    }
    if (form.next.length < 6) {
      toast.error("New password must be at least 6 characters");
      return;
    }
    setBusy(true);
    try {
      await api.post("/auth/change-password", {
        current_password: form.current,
        new_password: form.next,
      });
      toast.success("Password updated");
      setForm({ current: "", next: "", confirm: "" });
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed to change password");
    } finally {
      setBusy(false);
    }
  };

  const field = (label, key, testid) => (
    <label className="block mb-4">
      <div className="overline mb-1">{label}</div>
      <input
        data-testid={testid}
        type="password"
        required
        autoComplete="new-password"
        value={form[key]}
        onChange={(e) => setForm({ ...form, [key]: e.target.value })}
        className="w-full bg-transparent border-b-2 border-black/20 focus:border-black outline-none py-2 text-lg font-mono"
      />
    </label>
  );

  return (
    <form onSubmit={submit} className="bg-white border-2 border-black p-6" data-testid="password-form">
      <div className="overline mb-2 flex items-center gap-2"><KeyRound size={12} /> PASSWORD</div>
      <h3 className="font-display font-bold text-2xl tracking-tighter mb-6">Change password</h3>
      {field("CURRENT PASSWORD", "current", "current-password")}
      {field("NEW PASSWORD (min 6)", "next", "new-password")}
      {field("CONFIRM NEW PASSWORD", "confirm", "confirm-password")}
      <button
        data-testid="change-password-submit"
        disabled={busy}
        className="bg-black text-white px-6 py-3 font-display font-bold tracking-wider hover:bg-[var(--action-blue)] transition-colors disabled:opacity-50"
      >
        {busy ? "UPDATING…" : "UPDATE PASSWORD →"}
      </button>
    </form>
  );
}

function formatDate(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleDateString(undefined, {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  } catch {
    return "—";
  }
}

function AdminUsersPanel({ currentUserId }) {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [busyId, setBusyId] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const { data } = await api.get("/admin/users");
      setUsers(data || []);
    } catch (e) {
      setError(formatApiErrorDetail(e.response?.data?.detail) || "Failed to load users");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const toggleRole = async (u) => {
    const nextRole = u.role === "admin" ? "user" : "admin";
    setBusyId(u.id);
    try {
      await api.patch(`/admin/users/${u.id}/role`, { role: nextRole });
      setUsers((prev) => prev.map((x) => (x.id === u.id ? { ...x, role: nextRole } : x)));
      toast.success(`${u.email} is now ${nextRole.toUpperCase()}`);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed to update role");
    } finally {
      setBusyId(null);
    }
  };

  return (
    <div className="bg-white border-2 border-black p-6" data-testid="admin-users-panel">
      <div className="flex items-center justify-between mb-2">
        <div className="overline flex items-center gap-2"><Shield size={12} /> ADMIN ONLY</div>
        <div
          data-testid="admin-users-count"
          className="font-mono text-xs bg-black text-white px-2 py-1 flex items-center gap-1"
        >
          <Users size={12} /> {users.length} TOTAL
        </div>
      </div>
      <h3 className="font-display font-bold text-2xl tracking-tighter mb-6">Registered users</h3>

      {loading && (
        <div className="font-mono text-sm text-[var(--text-secondary)]" data-testid="admin-users-loading">
          LOADING…
        </div>
      )}

      {error && !loading && (
        <div className="font-mono text-sm text-[var(--red-flag,#c11)]" data-testid="admin-users-error">
          {error}
        </div>
      )}

      {!loading && !error && users.length === 0 && (
        <div className="font-mono text-sm text-[var(--text-secondary)]">No users found.</div>
      )}

      {!loading && !error && users.length > 0 && (
        <div className="overflow-x-auto -mx-6">
          <table className="w-full border-collapse font-mono text-sm" data-testid="admin-users-table">
            <thead>
              <tr className="border-y-2 border-black bg-black/5">
                <th className="text-left py-2 px-6 overline">EMAIL</th>
                <th className="text-left py-2 px-3 overline">ROLE</th>
                <th className="text-left py-2 px-3 overline">PREMIUM</th>
                <th className="text-left py-2 px-3 overline">JOINED</th>
                <th className="text-right py-2 px-6 overline">ACTION</th>
              </tr>
            </thead>
            <tbody>
              {users.map((u) => {
                const isSelf = u.id === currentUserId;
                const isAdmin = u.role === "admin";
                return (
                  <tr
                    key={u.id}
                    className="border-b border-black/10 hover:bg-black/[0.02]"
                    data-testid={`admin-user-row-${u.id}`}
                  >
                    <td className="py-3 px-6">
                      <div className="font-bold truncate max-w-[220px]">{u.email}</div>
                      {u.name && <div className="text-xs text-[var(--text-secondary)]">{u.name}</div>}
                    </td>
                    <td className="py-3 px-3">
                      <span
                        className={`inline-block px-2 py-0.5 text-xs font-bold ${
                          isAdmin ? "bg-black text-white" : "border border-black/30"
                        }`}
                      >
                        {u.role.toUpperCase()}
                      </span>
                    </td>
                    <td className="py-3 px-3">
                      {u.is_premium ? (
                        <span className="inline-block px-2 py-0.5 text-xs font-bold bg-[var(--action-blue,#0057ff)] text-white">
                          PRO
                        </span>
                      ) : (
                        <span className="text-[var(--text-secondary)] text-xs">FREE</span>
                      )}
                    </td>
                    <td className="py-3 px-3 text-xs text-[var(--text-secondary)]">
                      {formatDate(u.created_at)}
                    </td>
                    <td className="py-3 px-6 text-right">
                      {isSelf ? (
                        <span className="text-xs text-[var(--text-secondary)]">— YOU —</span>
                      ) : (
                        <button
                          data-testid={`admin-toggle-role-${u.id}`}
                          onClick={() => toggleRole(u)}
                          disabled={busyId === u.id}
                          className={`px-3 py-1.5 text-xs font-display font-bold tracking-wider border-2 border-black transition-colors disabled:opacity-50 ${
                            isAdmin
                              ? "bg-white text-black hover:bg-black hover:text-white"
                              : "bg-black text-white hover:bg-[var(--action-blue)]"
                          }`}
                        >
                          {busyId === u.id
                            ? "…"
                            : isAdmin
                            ? "REVOKE ADMIN"
                            : "MAKE ADMIN"}
                        </button>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

export default function Settings() {
  const { user, updateCurrency } = useAuth();
  const [currency, setCurrency] = useState(user?.currency || "USD");
  const [saving, setSaving] = useState(false);

  const save = async () => {
    setSaving(true);
    try {
      await updateCurrency(currency);
      toast.success("Currency updated");
    } catch (e) {
      toast.error("Failed to save");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-6 max-w-3xl" data-testid="settings-page">
      <div>
        <div className="overline">PREFERENCES</div>
        <h1 className="font-display font-black text-4xl sm:text-5xl tracking-tighter">Settings</h1>
      </div>

      <div className="bg-white border-2 border-black p-6">
        <div className="overline mb-2">CURRENCY</div>
        <h3 className="font-display font-bold text-2xl tracking-tighter mb-4">{currency}</h3>
        <div className="grid grid-cols-2 sm:grid-cols-5 gap-2">
          {CURRENCIES.map((c) => (
            <button
              key={c.code}
              data-testid={`currency-${c.code}`}
              onClick={() => setCurrency(c.code)}
              className={`border-2 p-3 text-left transition-colors ${
                currency === c.code
                  ? "border-black bg-black text-white"
                  : "border-black/15 hover:border-black"
              }`}
            >
              <div className="font-mono font-bold text-lg">{c.code}</div>
              <div className="text-xs font-mono opacity-70">{c.symbol}</div>
            </button>
          ))}
        </div>
        <button
          data-testid="save-currency"
          onClick={save}
          disabled={saving}
          className="mt-6 bg-black text-white px-6 py-3 font-display font-bold tracking-wider hover:bg-[var(--action-blue)] transition-colors disabled:opacity-50"
        >
          {saving ? "SAVING…" : "SAVE →"}
        </button>
      </div>

      <div className="bg-white border border-black/15 p-6">
        <div className="overline mb-2">ACCOUNT</div>
        <div className="font-mono text-sm space-y-1">
          <div><span className="text-[var(--text-secondary)]">EMAIL:</span> {user?.email}</div>
          <div><span className="text-[var(--text-secondary)]">NAME:</span> {user?.name}</div>
          <div><span className="text-[var(--text-secondary)]">ROLE:</span> {(user?.role || "user").toUpperCase()}</div>
        </div>
      </div>

      <PasswordCard />

      {user?.role === "admin" && <AdminUsersPanel currentUserId={user?.id} />}
    </div>
  );
}
